/**
 * Week, month and day keys for the date-only strings the app stores
 * (`ascent.date`, `climbLog.date` — "YYYY-MM-DD", written in the GYM's day).
 *
 * ONE copy for the client (`apps/web/src/utils.js` re-exports the week and
 * month keys, so its importers did not change) and the Cloud Functions (the TV
 * board's server-side aggregate, `tvBoard.js`, which must window ascents
 * exactly the way the gym TV did before the aggregate existed; the e-mail
 * package's Wrapped, which keys its active days with `dateKeyInTimezone`).
 *
 * `dateKeyInTimezone` is the timezone-correct key: it uses `Intl`, which the
 * purity rule of this package allows. It moved here from the `api` codebase's
 * `shared/gymTime.js` because the `email` codebase cannot import that folder.
 *
 * The week and month bodies are the client's original, unchanged. It reads the parsed date with
 * LOCAL getters, which is exact for a date-only string wherever the local zone
 * is at or east of UTC (a date-only string parses as UTC midnight, so the local
 * calendar day is the same one) — Hungary, and the Cloud Functions container,
 * which runs in UTC. A browser west of UTC would see the previous day; that is
 * the client's long-standing behaviour and deliberately not changed by the move.
 */

/**
 * Get ISO date string for the Monday of the week containing date `d` (ISO week).
 * Returns `null` for a missing/invalid date instead of letting the final
 * `toISOString()` throw a RangeError — a single bad ascent date must never crash
 * an entire stats view. Callers either compare the result (null never matches a
 * real week key) or filter it out before use.
 */
export function getWeekStart(d) {
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return null;
  const day = dt.getDay();
  const diff = dt.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(dt.setDate(diff)).toISOString().split("T")[0];
}

/** Extract "YYYY-MM" month key from an ISO date string. */
export function getMonthKey(d) { return d.substring(0, 7); }

/**
 * Calendar day ("YYYY-MM-DD") of an instant, in the given IANA timezone.
 *
 * This is the server's answer to the client's `todayStr(timezone)` in
 * src/utils.js. Every date-only field in the schema (`event.startDate`,
 * `registrationDeadline` on events, `route.createdAt`, `climbLog.date`) is
 * written in the GYM's day, so a predicate that compares "today" against
 * one of them must take today from the same clock — a Cloud Function's own
 * `new Date()` is the container's UTC day, which is already tomorrow for a
 * Budapest gym between 22:00 and midnight in summer.
 *
 * Assembled from `formatToParts`, NOT from a locale-formatted string: the
 * client's `todayStr` learned in production that a bare `en-CA` date can
 * render as "M/D/YYYY" on some runtimes, and a key in that shape sorts
 * outside every ISO window. Falls back to the UTC day when the timezone is
 * missing or unknown (a throw would take out a whole nightly job).
 */
export function dateKeyInTimezone(date, timezone) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  if (timezone) {
    try {
      const p = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit",
      }).formatToParts(d).reduce((acc, part) => { acc[part.type] = part.value; return acc; }, {});
      if (p.year && p.month && p.day) {
        return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
      }
    } catch (_) { /* unknown zone → UTC below */ }
  }
  return d.toISOString().slice(0, 10);
}
